import { useRef, useEffect, useState, useCallback } from 'react';
import moment from 'moment/moment';
import toast from 'react-hot-toast';
import { Drawer, Button } from '@mui/material';
import { useGetKidVideoThumbnailMutation } from '../../queries';
import { VIDEO_RECORD_TYPE } from '../../data/define';
import IcClose from '../../assets/image/ic_x.svg';
import IcRecord from '../../assets/image/ic_play_btn.svg';
import IcStop from '../../assets/image/ic_stop_btn.svg';
import IcCameraChange from '../../assets/image/ic_camera_change.svg';
import IcGallery from '../../assets/image/ic_gallery_btn.svg';
import TwoBtnAlert from '../common/TwoBtnAlert';
import RecordingGuideCarousel from './RecordingGuideCarousel';
import RecordingQuestionCarousel from './RecordingQuestionCarousel';
import RepImageSelectDialog from './RepImageSelectDialog';
import Loading from '../common/Loading';
import { useTranslation } from '../../hook/useTranslation';

interface RepImages {
  success?: boolean;
  thumbnails?: string[];
  bboxes?: any[];
  message?: string;
}

interface VideoHandleDialogProps {
  type: number | string | null;
  open: boolean;
  onClose?: () => void;
  kidId?: number | string | null;
  mi?: (number | string)[];
  reportId?: number | string | null;
  setCautionOpen: (open: boolean) => void;
}

const ViewMode = {
  Camera: 1,
  Gallery: 2,
  Options: 3,
};

const CameraMode = {
  Back: 'environment',
  Front: 'user',
};

function VideoHandleDialog({
  type,
  open,
  onClose = () => {},
  kidId = null,
  mi = [],
  reportId = null,
  setCautionOpen,
}: VideoHandleDialogProps) {
  const { t } = useTranslation();
  const { width, height } = window.screen; // 사용자 화면크기
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const galleryInputRef = useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState(ViewMode.Options);
  const [recording, setRecording] = useState(false);
  const [isBackCamera, setIsBackCamera] = useState(true);
  const [recordedVideoSaveAlert, setRecordedVideoSaveAlert] = useState(false); // 촬영영상 저장 얼럿
  const [savedVideo, setSavedVideo] = useState<Blob | File | null>(null); // 촬영한 영상
  const [savedMimeType, setSavedMimeType] = useState<string | null>(null); // 촬영한 영상 mimeType
  const [openRepImageSelectDialog, setOpenRepImageSelectDialog] = useState(false); // 대표 이미지 선택 다이얼로그
  const [repImages, setRepImages] = useState<RepImages>({});
  const isDailyType =
    type === null || (type !== null && Number(type) === VIDEO_RECORD_TYPE.DAILY) ? true : false; // true: 일상 영상, false: 영상 문답

  const useCamera = () => {
    setMode(ViewMode.Camera);
  };

  const useGallery = () => {
    galleryInputRef.current?.click();
  };

  // 카메라 스트림 초기화 / 정리
  useEffect(() => {
    if (mode !== ViewMode.Camera || !open) return;
    // 스트림 가져오기
    navigator.mediaDevices
      .getUserMedia({
        video: {
          width: { ideal: height },
          height: { ideal: width },
          facingMode: isBackCamera ? CameraMode.Back : CameraMode.Front,
          aspectRatio: 16 / 9,
        },
        audio: true,
      })
      .then((stream) => {
        streamRef.current = stream;
        if (videoRef.current) videoRef.current.srcObject = stream;
      })
      .catch((err) => {
        console.error('getUserMedia error:', err);
        toast.error(t('upload.video_handle.toast_camera_fail'));
        setMode(ViewMode.Options);
      });
    return () => {
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    };
  }, [mode, open, width, height, isBackCamera, t]);

  // 녹화 시작
  const startRecording = () => {
    if (!streamRef.current) {
      toast.error(t('upload.video_handle.toast_stream_not_found'));
      return;
    }
    chunksRef.current = [];
    const mimeType = 'video/mp4';
    const recorder = new MediaRecorder(streamRef.current, { mimeType });
    recorderRef.current = recorder;
    recorder.ondataavailable = (e: BlobEvent) => chunksRef.current.push(e.data);
    recorder.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: mimeType });
      setSavedVideo(blob);
      setSavedMimeType(mimeType);
    };
    recorder.start();
    setRecording(true);
  };

  // 녹화 종료
  const stopRecording = () => {
    recorderRef.current?.stop();
    setRecording(false);
    setRecordedVideoSaveAlert(true);
  };

  // 녹화 창 닫기
  const closeRecording = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setMode(ViewMode.Options);
    onClose();
  };

  const { mutate: getKidVideoThumbnail } = useGetKidVideoThumbnailMutation();

  // 녹화된 비디오 전송
  const handleSubmitRecordedVideo = useCallback(
    (attachedVideo?: Blob | File) => {
      const videoToSubmit = attachedVideo || savedVideo;
      if (!videoToSubmit) return;
      setLoading(true);
      const fd = new FormData();
      fd.append('file', videoToSubmit);
      getKidVideoThumbnail(fd, {
        onSuccess: (res: any) => {
          if (res && res.data?.success) {
            const base64Thumbnails = res.data.thumbnails.map(
              (img: string) => `data:image/jpeg;base64,${img}`
            );
            setRepImages({ ...res.data, thumbnails: base64Thumbnails });
            setRecordedVideoSaveAlert(false);
            setOpenRepImageSelectDialog(true);
          } else {
            toast.error(t('upload.video_handle.toast_thumbnail_fail'));
          }
        },
        onError: (error) => {
          toast.error(t('upload.video_handle.toast_thumbnail_fail'));
          console.error('Failed to make thumbnail:', error);
        },
        onSettled: () => {
          setLoading(false);
        },
      });
    },
    [savedVideo, getKidVideoThumbnail, t]
  );

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        setSavedVideo(file);
        handleSubmitRecordedVideo(file);
      }
      e.target.value = '';
    },
    [handleSubmitRecordedVideo]
  );

  const handleNotSaveRecordedVideo = useCallback(() => {
    setRecordedVideoSaveAlert(false);
    handleSubmitRecordedVideo();
  }, [handleSubmitRecordedVideo]);

  const handleSaveRecorededVideo = useCallback(() => {
    // savedVideo 기기에 저장
    if (!savedVideo || !savedMimeType) {
      toast.error(t('upload.video_handle.toast_no_video'));
      return;
    }
    let ext = savedMimeType.split('/')[1];
    ext = ext.split(';')[0];
    if (window.ReactNativeWebView) {
      const fileName = `onkids_video_${moment().format('YYYYMMDD_HHmmss')}.${ext}`;
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        const base64Data = result.split(',')[1];
        window.ReactNativeWebView?.postMessage(
          JSON.stringify({
            type: 'SAVE_VIDEO',
            fileName: fileName,
            videoData: base64Data,
            mimeType: savedMimeType,
          })
        );
      };
      reader.readAsDataURL(savedVideo);
    } else {
      const url = URL.createObjectURL(savedVideo);
      const a = document.createElement('a');
      a.href = url;
      a.download = `onkids_video_${moment().format('YYYYMMDD_HHmmss')}.${ext}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }

    // 비디오 전송
    setRecordedVideoSaveAlert(false);
    setOpenRepImageSelectDialog(false);
    handleSubmitRecordedVideo();
  }, [savedVideo, savedMimeType, handleSubmitRecordedVideo, t]);

  return (
    <Drawer
      open={open}
      anchor="bottom"
      onClose={(_event: {}, reason: 'backdropClick' | 'escapeKeyDown') => {
        if (reason === 'backdropClick') return;
        onClose();
        setMode(ViewMode.Options);
      }}
      PaperProps={{
        sx: {
          backgroundColor: 'transparent',
          boxShadow: 'none',
        },
      }}
    >
      <input
        type="file"
        accept="video/*"
        ref={galleryInputRef}
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />
      {mode === ViewMode.Options && (
        <div className="w-full p-5 min-h-[200px] flex flex-col items-center justify-end bg-[] overflow-hidden">
          <Button
            variant="grayPainted"
            className="w-full"
            onClick={useCamera}
            sx={{ borderRadius: '10px 10px 0px 0px', borderBottom: '2px solid #E9E9E9' }}
          >
            {t('upload.video_handle.record_btn')}
          </Button>
          <Button
            variant="grayPainted"
            className="w-full"
            onClick={useGallery}
            sx={{ borderRadius: '0px 0px 10px 10px' }}
          >
            {t('upload.video_handle.gallery_btn')}
          </Button>
          <Button
            variant="grayPainted"
            className="w-full"
            sx={{ mt: '17px' }}
            onClick={() => {
              setMode(ViewMode.Options);
              onClose();
            }}
          >
            {t('common.close')}
          </Button>
        </div>
      )}
      {mode === ViewMode.Camera && (
        <div className="relative w-full h-[100vh] bg-black">
          <div className="absolute top-0 left-0 right-0 p-4 flex bg-[#000] justify-between items-center z-10">
            <div className="w-[24px] h-[24px]" />
            <div className="text-white">{t('upload.video_handle.title')}</div>
            <button type="button" onClick={closeRecording}>
              <img src={IcClose} alt="Close" className="w-[24px] h-[24px]" />
            </button>
          </div>
          {/* realtime camera preview */}
          <video
            ref={videoRef}
            autoPlay
            muted
            playsInline
            className="h-[100dvh] w-full object-cover bg-black"
            style={{ width: '100dvw' }}
          />
          {/* overlay custom design */}
          <div className="absolute top-[80px] left-0 right-0 flex flex-col items-center justify-start">
            {isDailyType ? (
              <div className="w-full flex justify-center items-start overflow-x-auto">
                <RecordingGuideCarousel />
              </div>
            ) : (
              <>
                {!recording ? (
                  <div className="w-full flex justify-center items-start overflow-x-auto">
                    <RecordingGuideCarousel />
                  </div>
                ) : (
                  <div className="w-full flex justify-center items-start overflow-x-auto">
                    <RecordingQuestionCarousel intelligenceId={mi[0]} />
                  </div>
                )}
              </>
            )}
          </div>
          {/* record buttons */}
          <div
            className="absolute bottom-0 left-0 right-0 px-5 py-4 flex justify-between items-center gap-2"
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
          >
            <button
              type="button"
              onClick={useGallery}
              style={{ backgroundColor: 'rgba(255, 255, 255, 0.1)' }}
              className="w-[50px] h-[50px] flex items-center justify-center rounded-md"
            >
              <img src={IcGallery} alt="IcGallery" className="w-[20px] h-[20px]" />
            </button>
            {!recording ? (
              <button type="button" onClick={startRecording} className="relative ">
                <img src={IcRecord} alt="Record" className="w-[67px] h-[67px]" />
              </button>
            ) : (
              <button type="button" onClick={stopRecording} className="relative">
                <img src={IcStop} alt="Stop" className="w-[67px] h-[67px]" />
              </button>
            )}
            <button type="button" onClick={() => setIsBackCamera(!isBackCamera)} className="">
              <img
                src={IcCameraChange}
                alt="Change Camera"
                className="w-[45px] h-[45px]"
                style={{
                  transform: isBackCamera ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.6s ease',
                }}
              />
            </button>
          </div>
        </div>
      )}
      <TwoBtnAlert
        open={recordedVideoSaveAlert}
        onClose={() => setRecordedVideoSaveAlert(false)}
        title={t('upload.video_handle.save_alert_title')}
        content={t('upload.video_handle.save_alert_content')}
        leftBtnText={t('common.cancel')}
        rightBtnText={t('upload.video_handle.save_btn')}
        leftBtnClick={handleNotSaveRecordedVideo}
        rightBtnClick={handleSaveRecorededVideo}
      />
      <RepImageSelectDialog
        open={openRepImageSelectDialog}
        onClose={() => {
          setOpenRepImageSelectDialog(false);
          setCautionOpen(true);
          onClose();
        }}
        thumbnails={repImages.thumbnails || []}
        bboxes={repImages.bboxes || []}
        savedVideo={savedVideo}
        kidId={Number(kidId)}
        mi={mi as number[]}
        reportId={Number(reportId)}
      />
      {loading && <Loading />}
    </Drawer>
  );
}

export default VideoHandleDialog;
